/**
 * LLM-facing parameter schema for JoinDatasetsTool.
 *
 * Combines two stored datasets on a shared key column. Pure join — for any
 * filter / derive / sort / rollup work on the result, the LLM chains a
 * follow-up `transformDataset` call on `outputDataset`.
 */
import { z } from 'zod';

import { inputDatasetSchema, outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetProducerResult } from '#plugin-chat/lib/tool/output-types.ts';

export const JoinDatasetsInputSchema = z.object({
  leftDataset: inputDatasetSchema.describe('Key of the source (left) stored dataset.'),
  rightDataset: inputDatasetSchema.describe('Key of the right stored dataset to join with the left.'),
  on: z
    .object({
      left: z.string().describe('Join key column in the left dataset.'),
      right: z.string().describe('Join key column in the right dataset.'),
    })
    .describe('Column pair to match rows on. Use the same name for both when the key column is identical.'),
  how: z
    .enum(['left', 'inner', 'right', 'full'])
    .default('left')
    .describe(
      '"left" (default) keeps all left rows, adding nulls for non-matches. ' +
        '"inner" keeps only rows that match in both datasets. ' +
        '"right" / "full" mirror SQL semantics.',
    ),
  outputDataset: outputDatasetSchema,
});

/** @public */
export type JoinDatasetsInput = z.infer<typeof JoinDatasetsInputSchema>;

/**
 * @public Output type from the join datasets tool.
 *
 * Aliases the shared {@link DatasetProducerResult} contract — JoinDatasetsTool
 * stages the joined dataset envelope without adding tool-specific fields.
 */
export type JoinDatasetsOutput = DatasetProducerResult;
